import * as React from "react";
import { useState } from "react";
import { Text } from "@fluentui/react-components";
import { Box } from "@mui/material";
import { useFilterStyles } from "../styles/filterStyles";
import SpreetailHeader from "../layout/SpreetailHeader";
import SpreetailLoader from "../common/SpreetailLoader";
import { createEnhancedSalesPerformanceDashboardWithParams } from "../../dashboard";
import { refreshSalesDataSheet, isSalesDataSheet } from "../../rowdata";
import DateRangeFilter from "../filters/controls/DateRangeFilter";
import BrandFilter from "../filters/controls/BrandFilter";
import SkusFilter from "../filters/controls/SkusFilter";
import { useSalesFilters } from "../../hooks/useSalesFilters";

const SalesFilterRoute: React.FC = () => {
const filterStyles = useFilterStyles();

  const {
    startDate,
    endDate,
    brand,
    skus,
    channel,
    setStartDate,
    setEndDate,
    setBrand,
    setSkus,
    clearFilters,
    hasActiveFilters,
    isLoading: filtersLoading,
  } = useSalesFilters();

  const [isGenerating, setIsGenerating] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);

  const handleClearFilters = async () => {
    await clearFilters();
    setShowSuccess(false);

    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 2000);
  };

  const handleApplyFilter = async () => {
    console.log("🚀 APPLYING SALES DASHBOARD FILTER");
    console.log("📅 Start Date:", startDate);
    console.log("📅 End Date:", endDate);
    console.log("📋 SKUs:", skus);
    console.log("🏷️ Brand:", brand);
    console.log("📺 Channel:", channel);

    setIsGenerating(true);
    setShowSuccess(false);

    try {
      const params: any = {
        skus: skus && skus.length > 0 ? skus : [],
        start_purchase_date: startDate ? startDate.toISOString() : "",
        end_purchase_date: endDate ? endDate.toISOString() : "",
      };

      console.log("💾 Full Params:", params);

      await OfficeRuntime.storage.setItem("dash_filter", JSON.stringify(params));

      const individualFilters = {
        asinSkuValues: params.skus,
        startDate: startDate,
        endDate: endDate,
        brand: brand,
        channel: channel,
        filterType: "Sales Dashboard Filter",
        appliedAt: new Date().toISOString(),
      };
      await OfficeRuntime.storage.setItem("individual_filters", JSON.stringify(individualFilters));

      console.log("Generating Sales Dashboard with filters:", params);

      await Excel.run(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        activeSheet.load("name");
        await context.sync();

        const currentName = activeSheet.name;
        if (isSalesDataSheet(currentName)) {
          const dr =
            startDate && endDate
              ? `${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} - ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
              : undefined;
          await refreshSalesDataSheet(context, activeSheet, dr);
        } else {
          await createEnhancedSalesPerformanceDashboardWithParams(params);
        }
      });

      console.log("✅ Sales Dashboard generated successfully with applied filters!");

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("❌ Error generating filtered Sales Dashboard:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  if (filtersLoading) {
    return (
      <div className={filterStyles.filterContainer} style={{ height: "100%", minHeight: 0 }}>
        <SpreetailHeader />
        <div className={filterStyles.filterHeader}>
          <Text size={400} weight="semibold" style={{ color: "#0F172A" }}>
            Sales Dashboard
          </Text>
          <Text size={200} style={{ color: "#667085", opacity: 0.9, fontSize: "12px" }}>
            Loading filter settings...
          </Text>
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
          }}
        >
          <SpreetailLoader
            title="Loading filter settings"
            subtitle="Please wait while we prepare your filters"
            size="large"
          />
        </div>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div
        className={filterStyles.filterContainer}
        style={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SpreetailHeader />
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
          }}
        >
          <SpreetailLoader
            title="Working on your data"
            subtitle="Please wait while we generate your data"
            size="large"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={filterStyles.filterContainer} style={{ height: "100%", minHeight: 0 }}>
      <SpreetailHeader />
      <div className={filterStyles.filterHeader}>
        <Text size={400} weight="semibold" style={{ color: "#0F172A" }}>
          Sales Data Filter
        </Text>
        <Text size={200} style={{ color: "#667085", opacity: 0.9, fontSize: "12px" }}>
          Apply filters to refine your Sales Data
        </Text>
      </div>

      {/* Top Action Buttons - Apply to the left of Clear */}
      <div
        style={{
          padding: "0 16px 16px 16px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
        }}
      >
        <button
          onClick={handleApplyFilter}
          disabled={isGenerating}
          style={{
            backgroundColor: "rgba(16,185,129,0.12)",
            border: "1.5px solid rgba(16,185,129,0.35)",
            borderRadius: "8px",
            color: "#065F46",
            fontSize: "11px",
            fontWeight: 500,
            padding: "4px 8px",
            textTransform: "none",
            height: "28px",
            lineHeight: 1.2,
            minWidth: "auto",
            cursor: isGenerating ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            opacity: isGenerating ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.12)";
          }}
        >
          {isGenerating ? "Applying Filters..." : "Apply Filter"}
        </button>

        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            style={{
              backgroundColor: "rgba(239,68,68,0.10)",
              border: "1.5px solid rgba(239,68,68,0.35)",
              borderRadius: "8px",
              color: "#7F1D1D",
              fontSize: "11px",
              fontWeight: 500,
              padding: "4px 8px",
              textTransform: "none",
              height: "28px",
              lineHeight: 1.2,
              minWidth: "auto",
              cursor: "pointer",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.16)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.10)";
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div
        className={filterStyles.filterContent}
        style={{ overflowY: "auto", flex: 1, minHeight: 0 }}
      >
        {/* Date Range */}
        <Box className={filterStyles.inputGroup}>
          <label className={filterStyles.labelText}>Date Range</label>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onChangeStart={setStartDate}
            onChangeEnd={setEndDate}
          />
        </Box>

        {/* ASIN/SKU */}
        <Box className={filterStyles.inputGroup}>
          <SkusFilter
            value={skus}
            onChange={setSkus}
            options={[
              "SP-001",
              "SP-002",
              "SP-003",
              "SP-004",
              "SP-005",
              "HO-001",
              "HO-002",
              "HO-003",
              "HO-004",
              "HO-005",
              "KI-001",
              "KI-002",
              "KI-003",
              "KI-004",
              "KI-005",
              "TECH-001",
              "TECH-002",
              "TECH-003",
              "TECH-004",
              "TECH-005",
            ]}
          />
        </Box>

        {/* Brand (Static for now) */}
        <Box className={filterStyles.inputGroup}>
          <BrandFilter value={brand} onChange={setBrand} />
        </Box>

        {/* Channel (Static for now) */}
        {/* <Box className={filterStyles.inputGroup}>
          <ChannelFilter value={channel} onChange={setChannel} />
        </Box>
         */}

        {/* Action Buttons moved to header area above */}
      </div>
    </div>
  );
};

export default SalesFilterRoute;
